import React, { useState, useEffect, useRef } from "react";
import {
  makeStyles,
  Snackbar,
  SnackbarContent,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  snackbarContent: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    flexWrap: "nowrap",
    maxWidth: 480,
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
    },
  },
  messageContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  icon: {
    fontSize: 28,
    flexShrink: 0,
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 600,
    fontSize: "0.95rem",
    lineHeight: 1.3,
  },
  message: {
    fontSize: "0.8rem",
    opacity: 0.9,
    lineHeight: 1.3,
    marginTop: 2,
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginLeft: 8,
    flexShrink: 0,
  },
  installButton: {
    color: "#fff",
    borderColor: "rgba(255,255,255,0.7)",
    fontWeight: 600,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#fff",
      backgroundColor: "rgba(255,255,255,0.15)",
    },
  },
  closeButton: {
    color: "rgba(255,255,255,0.7)",
    "&:hover": {
      color: "#fff",
    },
  },
}));

const DISMISS_KEY = "pwa-install-dismissed";
const DISMISS_DURATION_DAYS = 7;

const isMobile = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
};

const isIOS = () => {
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const isDismissed = () => {
  try {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) return false;
    const dismissedDate = new Date(dismissed);
    const now = new Date();
    const diffDays = (now - dismissedDate) / (1000 * 60 * 60 * 24);
    return diffDays < DISMISS_DURATION_DAYS;
  } catch {
    return false;
  }
};

const setDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
  } catch {
    // localStorage not available
  }
};

// Captura global — garante que o evento não se perde se disparar antes do React montar
window.__pwaInstallEvent = window.__pwaInstallEvent || null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__pwaInstallEvent = e;
});

const InstallPWAPrompt = () => {
  const classes = useStyles();
  const deferredPromptRef = useRef(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  useEffect(() => {
    if (!isMobile()) return;
    if (isStandalone()) return;
    if (isDismissed()) return;

    // Se o evento já foi capturado globalmente antes do mount
    if (window.__pwaInstallEvent) {
      deferredPromptRef.current = window.__pwaInstallEvent;
      setShowPrompt(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      window.__pwaInstallEvent = e;
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS não dispara beforeinstallprompt — mostrar instruções manuais
    if (isIOS()) {
      const timer = setTimeout(() => {
        if (!isDismissed()) {
          setShowIOSPrompt(true);
        }
      }, 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;

    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") {
        setShowPrompt(false);
      }
    } catch (err) {
      console.error("[PWA] Erro ao instalar:", err);
    }

    deferredPromptRef.current = null;
    window.__pwaInstallEvent = null;
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSPrompt(false);
    setDismissed();
  };

  if (showPrompt) {
    return (
      <Snackbar
        open
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          className={classes.snackbarContent}
          message={
            <div className={classes.messageContainer}>
              <GetAppIcon className={classes.icon} />
              <div className={classes.textContainer}>
                <Typography className={classes.title}>
                  {i18n.t("installPWA.title")}
                </Typography>
                <Typography className={classes.message}>
                  {i18n.t("installPWA.message")}
                </Typography>
              </div>
            </div>
          }
          action={
            <div className={classes.actionContainer}>
              <Button
                variant="outlined"
                size="small"
                className={classes.installButton}
                onClick={handleInstall}
              >
                {i18n.t("installPWA.installButton")}
              </Button>
              <IconButton
                size="small"
                className={classes.closeButton}
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          }
        />
      </Snackbar>
    );
  }

  // iOS — instruções manuais (Safari não dispara beforeinstallprompt)
  if (showIOSPrompt) {
    const iosText = i18n
      .t("installPWA.iosInstructions")
      .replace("{{shareIcon}}", "⎋");

    return (
      <Snackbar
        open
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          className={classes.snackbarContent}
          message={
            <div className={classes.messageContainer}>
              <GetAppIcon className={classes.icon} />
              <div className={classes.textContainer}>
                <Typography className={classes.title}>
                  {i18n.t("installPWA.title")}
                </Typography>
                <Typography className={classes.message}>
                  {iosText}
                </Typography>
              </div>
            </div>
          }
          action={
            <IconButton
              size="small"
              className={classes.closeButton}
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Snackbar>
    );
  }

  return null;
};

export default InstallPWAPrompt;
